// frontend/src/components/Chart/PairTradingSection.jsx
//
// Thorp-style pair trading: pick a second symbol against the chart's symbol,
// get the cointegration verdict, the z-score spread strategy backtest
// (train vs out-of-sample), and feed the z-series up to the chart so it can
// draw the spread pane below RSI. The verdict is honest by design — most
// pairs are NOT cointegrated and the report says so instead of showing
// signals anyway.
import React, { useEffect, useState } from 'react';
import {
  ComposedChart, Bar, Line, Scatter, XAxis, YAxis, Tooltip,
  ReferenceLine, ReferenceArea, Cell, ResponsiveContainer,
} from 'recharts';
import { fetchPairTrading } from '../../services/api';
import useSymbolSuggestions from '../../hooks/useSymbolSuggestions';
import InfoHint from '../Common/InfoHint';

// Directional entry markers: green UP triangle = go long A, red DOWN triangle
// = go short A (the short arrow points the opposite way).
const UpTriangle = ({ cx, cy }) => (cx == null || cy == null ? null : (
  <path d={`M ${cx - 5} ${cy + 5} L ${cx + 5} ${cy + 5} L ${cx} ${cy - 5} Z`}
    fill="#16a34a" stroke="#fff" strokeWidth={0.6} />
));
const DownTriangle = ({ cx, cy }) => (cx == null || cy == null ? null : (
  <path d={`M ${cx - 5} ${cy - 5} L ${cx + 5} ${cy - 5} L ${cx} ${cy + 5} Z`}
    fill="#dc2626" stroke="#fff" strokeWidth={0.6} />
));

// Combined pairs chart: both legs' rebased closes as lines (left) with the
// spread z-score as light bars (right, shared centre), plus entry markers where
// a long/short on leg A would trigger.
const PairChart = ({ result }) => {
  const { priceSeries, zSeries, signals, thresholds, pair, holdoutFromTime } = result;
  if (!priceSeries?.length || !zSeries?.length) return null;

  const label = (t) => {
    const ms = typeof t === 'number' ? (t > 1e12 ? t : t * 1000) : new Date(t).getTime();
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
  };

  const priceByTime = new Map(priceSeries.map((p) => [String(p.time), p]));
  const sigByTime = new Map();
  (signals || []).forEach((s) => { sigByTime.set(String(s.time), s.type); });
  const data = zSeries.map((p) => {
    const pr = priceByTime.get(String(p.time)) || {};
    const t = sigByTime.get(String(p.time));
    return {
      time: p.time,
      a: pr.a ?? null,
      b: pr.b ?? null,
      spread: p.z,
      enterLong: t === 'ENTER_LONG_SPREAD' ? p.z : null,
      enterShort: t === 'ENTER_SHORT_SPREAD' ? p.z : null,
    };
  });
  const inOOS = holdoutFromTime != null &&
    data.some((d) => String(d.time) === String(holdoutFromTime));

  const priceVals = [];
  data.forEach((d) => { if (d.a != null) priceVals.push(d.a); if (d.b != null) priceVals.push(d.b); });
  const pMin = Math.min(...priceVals), pMax = Math.max(...priceVals);
  const pCenter = (pMin + pMax) / 2;
  const pHalf = (Math.max(pMax - pCenter, pCenter - pMin) || 1) * 1.06;
  const priceDomain = [pCenter - pHalf, pCenter + pHalf];

  const zAbs = data.reduce((m, d) => (Number.isFinite(d.spread) ? Math.max(m, Math.abs(d.spread)) : m), 0);
  const zHalf = Math.max(zAbs, thresholds.entry * 1.25) * 1.15;
  const zDomain = [-zHalf, zHalf];

  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-500 mb-1">
        <span className="flex items-center"><span className="w-3 h-0.5 bg-[#92400e] mr-1 inline-block" />{pair.a}</span>
        <span className="flex items-center"><span className="w-3 h-0.5 bg-[#2563eb] mr-1 inline-block" />{pair.b}</span>
        <span className="text-green-600">▲ enter long {pair.a}</span>
        <span className="text-red-600">▼ enter short {pair.a}</span>
      </div>
      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart data={data} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
          <XAxis dataKey="time" tickFormatter={label} tick={{ fontSize: 9 }} minTickGap={44} />
          <YAxis yAxisId="price" hide domain={priceDomain} allowDataOverflow />
          <YAxis yAxisId="z" orientation="right" hide domain={zDomain} allowDataOverflow />
          <Tooltip labelFormatter={label} contentStyle={{ fontSize: 11 }}
            formatter={(v, n) => {
              if (v == null) return [null, null];
              if (n === 'a') return [v, pair.a];
              if (n === 'b') return [v, pair.b];
              if (n === 'spread') return [typeof v === 'number' ? v.toFixed(2) : v, 'z-score'];
              return [null, null];
            }} />

          {/* out-of-sample shaded band after pair selection */}
          {inOOS && (
            <ReferenceArea yAxisId="z" x1={holdoutFromTime} x2={data[data.length - 1].time}
              fill="#6b7280" fillOpacity={0.06} />
          )}
          {inOOS && (
            <ReferenceLine yAxisId="z" x={holdoutFromTime} stroke="#9ca3af" strokeWidth={1} strokeDasharray="2 2"
              label={{ value: 'pair selection', position: 'insideTopRight', fontSize: 9, fill: '#9ca3af' }} />
          )}

          {/* spread z-score bars (right axis), red above / green below centre */}
          <Bar yAxisId="z" dataKey="spread" name="spread" barSize={2} isAnimationActive={false}>
            {data.map((d, i) => (
              <Cell key={i} fill={d.spread >= 0 ? 'rgba(220,38,38,0.28)' : 'rgba(22,163,74,0.28)'} />
            ))}
          </Bar>
          <ReferenceLine yAxisId="z" y={thresholds.entry} stroke="#dc2626" strokeDasharray="4 4" strokeOpacity={0.6} />
          <ReferenceLine yAxisId="z" y={-thresholds.entry} stroke="#16a34a" strokeDasharray="4 4" strokeOpacity={0.6} />

          {/* the two price lines */}
          <Line yAxisId="price" type="monotone" dataKey="a" name={pair.a} stroke="#92400e" strokeWidth={1.6} dot={false} isAnimationActive={false} />
          <Line yAxisId="price" type="monotone" dataKey="b" name={pair.b} stroke="#2563eb" strokeWidth={1.6} dot={false} isAnimationActive={false} />

          {/* entry markers — up = long A, down = short A (short arrow reversed) */}
          <Scatter yAxisId="z" dataKey="enterLong" name="enter long" shape={UpTriangle} isAnimationActive={false} />
          <Scatter yAxisId="z" dataKey="enterShort" name="enter short" shape={DownTriangle} isAnimationActive={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

const fmtPct = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(2)}%`);
const fmt = (v, d = 2) => (v == null ? '—' : v.toFixed(d));

const MetricRow = ({ label, train, holdout, format = (v) => v ?? '—' }) => (
  <tr className="border-t border-gray-100">
    <td className="py-1 pr-3 text-gray-500">{label}</td>
    <td className="py-1 pr-3 text-right font-mono">{format(train)}</td>
    <td className="py-1 text-right font-mono font-medium">{format(holdout)}</td>
  </tr>
);

const PairTradingSection = ({ symbol, onPairData }) => {
  const [input, setInput] = useState('');
  const [pairSymbol, setPairSymbol] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggest = useSymbolSuggestions();
  const suggestions = showSuggestions
    ? suggest(input).filter((s) => s.symbol !== symbol.toUpperCase())
    : [];

  const pickSuggestion = (sym) => {
    setInput(sym);
    setShowSuggestions(false);
    if (sym && sym !== symbol.toUpperCase()) setPairSymbol(sym);
  };

  // Changing the main symbol invalidates the pair.
  useEffect(() => {
    setResult(null);
    setPairSymbol('');
    setInput('');
    setError(null);
    onPairData(null);
  }, [symbol]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!pairSymbol) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchPairTrading(symbol, pairSymbol)
      .then((r) => {
        if (cancelled) return;
        if (r?.status === 'ok') {
          setResult(r);
          onPairData(r);
        } else {
          setResult(null);
          onPairData(null);
          setError(r?.status === 'insufficient_data'
            ? `Only ${r.overlapBars} overlapping daily bars — need ${r.minBars}.`
            : `No data for that pair (${r?.status || 'error'}).`);
        }
      })
      .catch(() => { if (!cancelled) setError('Pair analysis failed — check the symbol.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [symbol, pairSymbol]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = (e) => {
    e.preventDefault();
    const s = input.trim().toUpperCase();
    if (s && s !== symbol.toUpperCase()) setPairSymbol(s);
  };

  const coint = result?.cointegration;
  const bt = result?.backtest;

  return (
    <div className="border-t border-gray-200 px-4 py-3">
      <form onSubmit={submit} className="flex items-center space-x-2">
        <span className="flex items-start gap-1"><span className="text-sm font-medium text-gray-700">Pair Trading</span><InfoHint title="Pair trading" align="left">Ed Thorp-style statistical arbitrage: pick a second symbol and we test whether the two move together long-term (cointegration). If the spread between them stretches too far (z-score ±2), the strategy bets on it snapping back. The backtest shows train vs out-of-sample results honestly — most pairs are NOT cointegrated.</InfoHint></span>
        <span className="text-xs text-gray-400">{symbol} vs</span>
        <div className="relative">
          <input
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              setShowSuggestions(true);
            }}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
            placeholder="e.g. CTG.VN"
            className="w-32 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
          {suggestions.length > 0 && (
            <div className="absolute top-full left-0 mt-1 z-40 w-64 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
              {suggestions.map((s) => (
                <button
                  key={s.symbol}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault(); // beat the input's onBlur
                    pickSuggestion(s.symbol);
                  }}
                  className="w-full flex items-center justify-between px-3 py-1.5 text-xs hover:bg-blue-50 text-left"
                >
                  <span className="font-medium text-gray-800">{s.symbol}</span>
                  {s.name && <span className="text-gray-400 truncate ml-2">{s.name}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
        <button type="submit"
          className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
          disabled={loading || !input.trim()}>
          {loading ? 'Testing…' : 'Analyze'}
        </button>
        {error && <span className="text-xs text-red-500">{error}</span>}
      </form>

      {result && coint && (
        <>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          {/* Cointegration verdict + spread state */}
          <div className="text-xs space-y-2">
            <div className={`inline-block px-2 py-1 rounded font-medium ${
              coint.cointegrated ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
              {coint.cointegrated
                ? `Cointegrated (ADF p = ${coint.pValue})`
                : `NOT cointegrated (ADF p = ${coint.pValue}) — spread signals are unreliable for this pair`}
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-gray-600">
              <span>Hedge ratio (β)</span><span className="font-mono text-right">{fmt(coint.hedgeRatio, 3)}</span>
              <span>Half-life</span><span className="font-mono text-right">{coint.halfLifeBars ? `${coint.halfLifeBars} bars` : 'not mean-reverting'}</span>
              <span>Return correlation</span><span className="font-mono text-right">{fmt(coint.correlation)}</span>
              <span>Current z-score</span>
              <span className={`font-mono text-right font-semibold ${
                Math.abs(result.currentZ) >= 2 ? 'text-red-600' : 'text-gray-800'}`}>
                {fmt(result.currentZ)}
              </span>
            </div>
            <div className="text-[10px] text-gray-400">
              Enter at |z| ≥ {result.thresholds.entry}, exit at mean, stop at |z| ≥ {result.thresholds.stop}.
              β and z-window fitted on the first {result.validation.fitBars} bars —
              the last {result.validation.holdoutBars} bars are out-of-sample.
            </div>
          </div>

          {/* Backtest: train vs OOS */}
          <div className="text-xs">
            <table className="w-full">
              <thead>
                <tr className="text-gray-400">
                  <th className="text-left font-normal pb-1">Backtest</th>
                  <th className="text-right font-normal pb-1">Train</th>
                  <th className="text-right font-normal pb-1">Out-of-sample</th>
                </tr>
              </thead>
              <tbody>
                <MetricRow label="Trades" train={bt.train.trades} holdout={bt.holdout.trades} />
                <MetricRow label="Win rate" train={bt.train.winRate} holdout={bt.holdout.winRate}
                  format={(v) => (v == null ? '—' : `${Math.round(v * 100)}%`)} />
                <MetricRow label="Avg trade" train={bt.train.avgPct} holdout={bt.holdout.avgPct} format={fmtPct} />
                <MetricRow label="Total" train={bt.train.totalPct} holdout={bt.holdout.totalPct} format={fmtPct} />
                <MetricRow label="Sharpe" train={bt.train.sharpe} holdout={bt.holdout.sharpe}
                  format={(v) => fmt(v)} />
                <MetricRow label="Max drawdown" train={bt.train.maxDrawdownPct} holdout={bt.holdout.maxDrawdownPct}
                  format={(v) => (v == null ? '—' : `${v.toFixed(1)}%`)} />
              </tbody>
            </table>
            {result.notes?.length > 0 && (
              <div className="mt-2 text-[10px] text-gray-400">
                {result.notes.map((n, i) => <div key={i}>· {n}</div>)}
              </div>
            )}
          </div>
        </div>
        {/* The pair chart: two price lines + z-score with signals */}
        <PairChart result={result} />
        </>
      )}
    </div>
  );
};

export default PairTradingSection;
